/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useContext, useEffect } from 'react';
import Alert from 'react-s-alert';
import moment from 'moment';
import { Button, FormGroup, InputGroup, InputGroupText, Input, Table, Label } from 'reactstrap';
import Joyride, { STATUS } from 'react-joyride';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AuthService from '../../services/AuthService';
import ClientService from '../../services/ClientService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';
import Loading from '../../assets/UW_Horizontal_Logo_Full.png';
import SelectedProvider from './SelectedProvider';
import ClientNavbar from './ClientNavbar';
import FullPageLoader from '../general/FullPageLoader';


const MakePayment = props => {

    // constants
    const RAZORPAY_CDN = 'https://checkout.razorpay.com/v1/checkout.js';

    // contexts
    const { theme } = useContext(ThemeContext);
    const { user, setUser } = useContext(AuthContext);

    // states
    const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [voucherData, setVoucherData] = useState();
    const [voucher, setVoucher] = useState('');
    const [extraVoucher, setExtraVoucher] = useState(0);
    const [couponData, setCouponData] = useState();
    const [coupon, setCoupon] = useState('');
    const [totalAmount, setTotalAmount] = useState(0);
    const [GST, setGST] = useState({ percent: 0, amount: 0 });
    const [couponAmount, setCouponAmount] = useState(0);
    const [voucherAmount, setVoucherAmount] = useState(0);
    const [selectedProvider, setSelectedProvider] = useState({});
    const [sessionDetails, setSessionDetails] = useState({
        selectedDate: "",
        selectedTimeSlot: [],
        modeOfServiceSelected: {},
        isReportIncluded: {}
    });
    const [clientSubscription, setClientSubscription] = useState({ isActive: false, _id: '', razorpaySubscriptionId: '', isCallSessionAvailable: false, isVideoSessionAvailable: false, isInPersonSessionAvailable: false, reportStatus: false });
    const [isClientSubscriptionBooking, setIsClientSubscriptionBooking] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({});
    const [isFirstSession, setIsFirstSession] = useState(false);
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <div><h2>You can make payments using this page.</h2><br />Confirm your choice of session details and optionally apply Coupon Code or Gift Voucher Code, if any.</div>,
                placement: 'center',
                target: '#makePaymentDiv',
            },
            {
                content: <div className="tooltipDiv">Confirm your session details before proceeding.</div>,
                placement: 'bottom',
                target: '#sessionDetails'
            },
            {
                content: <div className="tooltipDiv">If you have a GiftVoucher from Umang Wellness you can apply it here.<br />You can always buy and gift it to someone from our <a href="/">Home page</a>.</div>,
                placement: 'right',
                target: '#giftVoucher'
            },
            {
                content: <div className="tooltipDiv">If you have a Coupon code from Umang Wellness you can apply it here</div>,
                placement: 'right',
                target: '#coupon'
            },
            {
                content: <div className="tooltipDiv">Here you will see the final summary of the payment</div>,
                placement: 'top',
                target: '#finalCharges'
            },
            {
                content: <div className="tooltipDiv">Press here to proceed to our payment gateway.<br />You may use UPI, Debit card, Credit card and Netbanking to pay the amount.<br />Payments are secured and powered by Razorpay</div>,
                placement: 'top',
                target: '#bookBtn'
            }
        ]
    });
    let helpers = null;

    // useEffect
    useEffect(() => {
        if (!user.guideTakenFor.includes("MakePayment")) {
            setTutorial({ ...tutorial, status: true });
        }
    }, []);

    useEffect(() => {
        try {
            setPaymentDetails({
                ...paymentDetails,
                providerEmail: selectedProvider.email
            })
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment useEffect1', 'Error in try catch block', err.stack);
        }
    }, [selectedProvider]);

    // useEffect(() => console.log("subscription", subscription), [subscription]);

    useEffect(() => {
        try {
            if (props.location.state) {
                ClientService.getSubscription().then(clientSubscription => {
                    if (clientSubscription.data) {
                        let _id = '';
                        let razorpaySubscriptionId = '';
                        let isCallSessionAvailable = false;
                        let isVideoSessionAvailable = false;
                        let isInPersonSessionAvailable = false;
                        let reportStatus = false;
                        if (clientSubscription.data.isActive) {
                            _id = clientSubscription.data._id;
                            razorpaySubscriptionId = clientSubscription.data.razorpaySubscriptionId;
                            isCallSessionAvailable = clientSubscription.data.isCallSessionAvailable;
                            isVideoSessionAvailable = clientSubscription.data.isVideoSessionAvailable;
                            isInPersonSessionAvailable = clientSubscription.data.isInPersonSessionAvailable;
                            reportStatus = clientSubscription.data.clientSubscriptionPlan.serviceDetails.report;
                        }
                        setClientSubscription({ isActive: clientSubscription.data.isActive, _id, razorpaySubscriptionId, isCallSessionAvailable, isVideoSessionAvailable, isInPersonSessionAvailable, reportStatus });
                        if (props.location.state.modeOfServiceSelected.name === "Call" && isCallSessionAvailable) {
                            setIsClientSubscriptionBooking(true);
                        } else if (props.location.state.modeOfServiceSelected.name === "Video" && isVideoSessionAvailable) {
                            setIsClientSubscriptionBooking(true);
                        } else if (props.location.state.modeOfServiceSelected.name === "In Person" && isInPersonSessionAvailable) {
                            setIsClientSubscriptionBooking(true);
                        }
                        // console.log("state", props.location.state, clientSubscription);
                    }
                    // setClientSubscription(clientSubscription.data);
                    const GSTPercentage = props.location.state.selectedProvider.GSTCharge;
                    let total = Number(props.location.state.modeOfServiceSelected.rate * props.location.state.selectedTimeSlot.length) + Number(props.location.state.isReportIncluded.amount);
                    let GST = (total * GSTPercentage) / 100;
                    // let totalWithGST = total + GST;
                    setSelectedProvider(props.location.state.selectedProvider);
                    setSessionDetails(props.location.state);
                    setIsFirstSession(props.location.state.isFirstSession);
                    if (props.location.state.isFirstSession) {
                        setGST({ percent: 0, amount: 0 });
                        setTotalAmount(0);
                    } else {
                        setGST({ percent: GSTPercentage, amount: GST });
                        setTotalAmount(total);
                    }
                    setIsLoading(false);
                });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment useEffect2', 'Error in try catch block', err.stack);
            setIsLoading(false);
        }
    }, [props.location.state]);

    // utility functions
    const loadScript = (src) => {
        try {
            return new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = () => {
                    resolve(true);
                }
                script.onerror = () => {
                    resolve(false);
                }
                document.body.appendChild(script);
            })
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment loadScript', 'Error in try catch block', err.stack);
        }
    }

    // handle functions
    const applyCoupon = () => {
        try {
            if (voucherAmount > 0) {
                Alert.error("You cannot apply Voucher and Coupon at same time", {
                    position: 'top-right',
                    effect: 'slide'
                });
            } else {
                ClientService.applyCoupon(coupon)
                    .then(data => {
                        if (data.data.type === "percentage") {
                            const couponAmount = totalAmount * (data.data.value / 100);
                            if (couponAmount > data.data.maxDiscount) {
                                setCouponData(data.data);
                                setCouponAmount(data.data.maxDiscount);
                                setGST({ ...GST, amount: (totalAmount - data.data.maxDiscount) * (GST.percent / 100) })
                            }
                            else {
                                setCouponData(data.data);
                                setCouponAmount(couponAmount);
                                setGST({ ...GST, amount: (totalAmount - couponAmount) * (GST.percent / 100) })
                            }
                        } else {
                            if (totalAmount < data.data.minPurchaseRequired) {
                                Alert.error(`Coupon applicable only on amount above ${data.data.minPurchaseRequired}`, {
                                    position: 'top-right',
                                    effect: 'slide'
                                });
                                return;
                            } else {
                                setCouponData(data.data);
                                setCouponAmount(data.data.value);
                                setGST({ ...GST, amount: (totalAmount - data.data.value) * (GST.percent / 100) });
                            }
                        }
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    })
                    .catch(error => {
                        Alert.error(error.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment applyCoupon', 'Error in try catch block', err.stack);
        }
    }

    const removeCoupon = () => {
        try {
            setCoupon();
            setCouponData();
            setCouponAmount(0);
            setGST({ ...GST, amount: (totalAmount - 0) * (GST.percent / 100) });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment removeCoupon', 'Error in try catch block', err.stack);
        }
    }

    const applyVoucher = () => {
        try {
            if (couponAmount > 0) {
                Alert.error("You cannot apply Coupon and Voucher at same time", {
                    position: 'top-right',
                    effect: 'slide'
                });
            } else {
                ClientService.applyVoucher(voucher)
                    .then(data => {
                        setVoucherData(data.data);
                        if (totalAmount < data.data.amount) {
                            setExtraVoucher(data.data.amount - totalAmount);
                            setVoucherAmount(totalAmount);
                        } else {
                            setVoucherAmount(data.data.amount);
                        }
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    })
                    .catch(error => {
                        Alert.error(error.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment applyVoucher', 'Error in try catch block', err.stack);
        }
    }

    const removeVoucher = () => {
        try {
            setVoucher();
            setVoucherData();
            setExtraVoucher(0)
            setVoucherAmount(0);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment removeVoucher', 'Error in try catch block', err.stack);
        }
    }

    // Function to trigger on Payment button click
    const displayRazorpay = async () => {
        try {
            setDisableSubmitBtn(true);
            if (isFirstSession) {
                bookSession("Free Session");
            } else if (isClientSubscriptionBooking) {
                bookClientSubscriptionSession({ clientSubscriptionId: clientSubscription._id, razorpaySubscriptionId: clientSubscription.razorpaySubscriptionId });
            } else if ((totalAmount - couponAmount - voucherAmount + GST.amount) === 0) {
                bookSession({ voucher: voucherData, coupon: couponData, amount: '0.00' });
            } else {
                setIsLoading(true);
                // load razorpay CDN
                const res = await loadScript(RAZORPAY_CDN);

                if (!res) {
                    console.log('Razorpay SDK failed to load');
                    return;
                }

                // This body will consist order details by passing this to razorpay we will generate a new order and it will return a token using which we will proceed with payment
                // https://github.com/razorpay/razorpay-node/blob/HEAD/documents/order.md#create-order
                let body = {
                    amount: totalAmount - couponAmount - voucherAmount + GST.amount,
                    currency: "INR",
                    email_id: user.email
                };

                ClientService.getPaymentToken(body)
                    .then(data => {
                        // For options details reference https://razorpay.com/docs/api/orders/#create-an-order
                        const options = {
                            key: data.key.toString(),
                            currency: data.currency,
                            amount: data.amount.toString(),
                            order_id: data.id,
                            name: user.email,
                            description: 'Payment against booksession',
                            // This handler will get called once payment is caputured successfully
                            handler: function (response) {
                                response.amount = Number(data.amount / 100).toFixed(2);
                                if (couponData) {
                                    couponData.value = couponAmount;
                                }
                                if (voucherData) {
                                    voucherData.value = voucherAmount;
                                }
                                response.coupon = couponData;
                                response.voucher = voucherData;
                                bookSession(response);
                            },
                            // Here methods will be defined to handle payment popup
                            modal: {
                                ondismiss: function () {
                                    setIsLoading(false);
                                    setDisableSubmitBtn(false);
                                    // console.log("Checkout form closed");
                                }
                            },
                            // Here we can define fields to get pre-filled on payment popup
                            prefill: {
                                name: user.username,
                                email: user.email
                                // phone_number: '9899999999'
                            }
                        }

                        const paymentObject = new window.Razorpay(options)
                        paymentObject.open()
                    })
                    .catch(error => { console.error(error); setIsLoading(false); setDisableSubmitBtn(false); })
            }
        } catch (err) {
            setDisableSubmitBtn(false);
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment displayRazorpay', 'Error in try catch block', err.stack);
        }
    }

    const bookSession = (paymentDetails) => {
        try {
            ClientService.setSession(sessionDetails, paymentDetails, extraVoucher)
                .then(data => {
                    Alert.info(data.message, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    removeCoupon();
                    removeVoucher();
                    setUser({ ...user, bookSessionDetails: null });
                    setIsLoading(false);
                    setDisableSubmitBtn(false);
                    props.history.push('dashboard');
                })
                .catch(error => {
                    setIsLoading(false);
                    setDisableSubmitBtn(false);
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                });
        } catch (err) {
            setDisableSubmitBtn(false);
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment bookSession', 'Error in try catch block', err.stack);
        }
    }

    const bookClientSubscriptionSession = (paymentDetails) => {
        try {
            // console.log("paymentDetails", paymentDetails);
            ClientService.bookSubscriptionSession(sessionDetails, paymentDetails)
                .then(data => {
                    // console.log("res data", data);
                    Alert.info(data.message, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setUser({ ...user, bookSessionDetails: null });
                    setIsLoading(false);
                    setDisableSubmitBtn(false);
                    props.history.push('dashboard');
                })
                .catch(error => {
                    console.log("res err", error);
                    setIsLoading(false);
                    setDisableSubmitBtn(false);
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                });
        } catch (err) {
            console.log("res err2", err);
            setDisableSubmitBtn(false);
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client MakePayment bookSession', 'Error in try catch block', err.stack);
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("MakePayment")) {
                AuthService.markVisited("MakePayment");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "MakePayment"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        isLoading ?
            <FullPageLoader />
            :
            <div id="makePaymentDiv">
                <ErrorBoundary errorAt="At Client MakePayment Joyride">
                    <Joyride
                        callback={handleJoyrideCallback}
                        continuous={true}
                        getHelpers={getHelpers}
                        run={tutorial.status}
                        scrollToFirstStep={true}
                        showProgress={true}
                        showSkipButton={true}
                        steps={tutorial.steps}
                        locale={{ last: 'Finish' }}
                        styles={{
                            options: {
                                primaryColor: "rgb(56, 78, 115)",
                                zIndex: 10000,
                            },
                        }}
                    />
                </ErrorBoundary>
                <ErrorBoundary errorAt="At Client MakePayment ClientNavbar">
                    <ClientNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
                </ErrorBoundary>
                <div className="p-3">

                    {/* Page Heading */}
                    <div>
                        <h1 id="make-payment-heading" style={theme.headingBold}>Make Payment</h1>
                        <hr style={{ borderWidth: "2px" }} />
                    </div>

                    {/* Confirm Session Details */}
                    <div className="p-3">
                        <h2 id="confirm-session-details-heading" style={theme.subHeadingSemibold}>Confirm Session Details</h2>
                        <hr />

                        <ErrorBoundary errorAt="At Client MakePayment SelectedProvider">
                            <SelectedProvider
                                selectedProvider={selectedProvider}
                            />
                        </ErrorBoundary>

                        <ErrorBoundary errorAt="At Client MakePayment Table">
                            <Table borderless className="mt-4" id="sessionDetails">
                                <tbody>
                                    <tr>
                                        <td style={{ minWidth: '10rem', width: '10rem' }}>
                                            <span id="session-date" style={theme.paragraphMDSM}>Session Date</span>
                                        </td>
                                        <td>
                                            <span id="session-date-value" style={theme.paragraphMDSM}>{moment(sessionDetails.selectedDate).utcOffset("+05:30").format('MMM DD, dddd')}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <span id="session-time" style={theme.h3HeadingSemibold}>Session Time</span>
                                        </td>
                                        <td>
                                            <span id="session-time-value" style={theme.paragraphMDSM}>{sessionDetails.selectedTimeSlot.map(item => `${moment(item.from).utcOffset("+05:30").format("hh:mm A")} - ${moment(item.to).utcOffset("+05:30").format("hh:mm A")}`).join(", ")}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <span id="mode-of-service" style={theme.h3HeadingSemibold}>Mode of Service</span>
                                        </td>
                                        <td>
                                            <span id="mode-of-service-value" style={theme.paragraphMDSM}>{sessionDetails.modeOfServiceSelected.name}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </ErrorBoundary>
                    </div>

                    {/* Payment */}
                    {
                        isClientSubscriptionBooking ? null :
                            <div className="p-3">
                                <h2 id="payment" style={theme.subHeadingSemibold}>Payment</h2>
                                <hr />
                                {
                                    isFirstSession || isClientSubscriptionBooking ? null :
                                        voucherData ?
                                            <div>
                                                <InputGroup style={{ maxWidth: "20rem" }}>
                                                    <Input disabled className="form-control" style={theme.formInput} value={voucher} />
                                                    <Button type="button" style={theme.formButtonDanger} onClick={removeVoucher}>Remove</Button>
                                                </InputGroup>
                                                <p className="my-1">
                                                    <i className="fas fa-check-circle" style={{ color: "#384E73" }}></i> Gift voucher applied worth ₹{voucherAmount}
                                                </p>
                                                {
                                                    extraVoucher > 0 ?
                                                        <p className="my-1">
                                                            <i className="fas fa-check-circle" style={{ color: "#384E73" }}></i> Remaining balance of ₹{extraVoucher} will be sent back as a gift voucher to your email
                                                        </p> : null
                                                }
                                            </div>
                                            :
                                            <FormGroup style={{ maxWidth: "20rem" }} id="giftVoucher">
                                                <Label id="gift-voucher" for="code" style={theme.formBodyText} >Have a gift voucher?</Label>
                                                <InputGroup>
                                                    <Input type="text" name="code" value={voucher} onChange={(e) => setVoucher(e.target.value)} className="form-control" style={theme.formInput} />
                                                    <Button type="button" style={theme.formButton} onClick={applyVoucher}>Apply</Button>
                                                </InputGroup>
                                            </FormGroup>
                                }
                                {isFirstSession || isClientSubscriptionBooking ? null : <div style={{ marginLeft: '7rem' }}>OR</div>}
                                {
                                    isFirstSession || isClientSubscriptionBooking ? null :
                                        couponData ?
                                            <div>
                                                <InputGroup className="mt-2" style={{ maxWidth: "20rem" }}>
                                                    <Input disabled className="form-control" style={theme.formInput} value={coupon} />
                                                    <Button type="button" style={theme.formButtonDanger} onClick={removeCoupon}>Remove</Button>
                                                </InputGroup>
                                                <p className="my-1">
                                                    <i className="fas fa-check-circle" style={{ color: "#384E73" }}></i> {couponData.description}
                                                </p>
                                            </div>
                                            :
                                            <FormGroup className="mt-2" style={{ maxWidth: "20rem" }} id="coupon">
                                                <Label id="coupon-code" for="code" style={theme.formBodyText} >Have coupon code?</Label>
                                                <InputGroup>
                                                    <Input type="text" name="code" value={coupon} onChange={(e) => setCoupon(e.target.value)} className="form-control" style={theme.formInput} />
                                                    <Button type="button" style={theme.formButton} onClick={applyCoupon}>Apply</Button>
                                                </InputGroup>
                                            </FormGroup>
                                }
                                {
                                    isClientSubscriptionBooking ?
                                        null
                                        :
                                        <ErrorBoundary errorAt="At Client MakePayment Table">
                                            <Table borderless className="mt-4" id="finalCharges">
                                                <tbody>
                                                    <tr>
                                                        <td style={{ minWidth: '12.5rem', width: '12.5rem' }}>
                                                            <span id="session-charges" style={theme.h3HeadingSemibold}>Session Charges</span>
                                                        </td>
                                                        <td>
                                                            <span id="session-charges-value" style={theme.paragraphMDSM}>₹
                                                                {
                                                                    isFirstSession
                                                                        ? 0
                                                                        : sessionDetails.modeOfServiceSelected.rate * sessionDetails.selectedTimeSlot.length
                                                                }
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    {
                                                        sessionDetails.isReportIncluded.status && !isFirstSession ?
                                                            <tr>
                                                                <td>
                                                                    <span id="report-charges" style={theme.h3HeadingSemibold}>Report Charges</span>
                                                                </td>
                                                                <td>
                                                                    <span id="report-charges-value" style={theme.paragraphMDSM}>₹{sessionDetails.isReportIncluded.amount}</span>
                                                                </td>
                                                            </tr>
                                                            : null
                                                    }
                                                    {
                                                        voucherAmount > 0 ?
                                                            <tr>
                                                                <td>
                                                                    <span style={theme.h3HeadingSemibold}>Gift Voucher applied</span>
                                                                </td>
                                                                <td>
                                                                    <span style={theme.paragraphMDSM}>₹{voucherAmount}</span>
                                                                </td>
                                                            </tr> : null
                                                    }
                                                    {
                                                        couponAmount > 0 ?
                                                            <tr>
                                                                <td>
                                                                    <span style={theme.h3HeadingSemibold}>Coupon Code applied</span>
                                                                </td>
                                                                <td>
                                                                    <span style={theme.paragraphMDSM}>₹{couponAmount}</span>
                                                                </td>
                                                            </tr> : null
                                                    }
                                                    <tr>
                                                        <td>
                                                            <span id="gst-lable" style={theme.h3HeadingSemibold}>GST({GST.percent}%)</span>
                                                        </td>
                                                        <td>
                                                            <span id="gst-value" style={theme.paragraphMDSM}>₹{GST.amount}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <span id="total-amount-due" style={theme.h3HeadingSemibold}>Total Amount Due</span>
                                                        </td>
                                                        <td>
                                                            <span id="total-amount-due-value" style={theme.paragraphMDSM}>₹{(totalAmount - couponAmount - voucherAmount + GST.amount)}</span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </ErrorBoundary>
                                }
                            </div>
                    }

                    <ErrorBoundary errorAt="At Client MakePayment BookBtn">
                        <button className="btn my-2" style={theme.button} disabled={disableSubmitBtn} onClick={displayRazorpay} id="bookBtn">
                            {
                                isFirstSession
                                    ?
                                    'Book for FREE'
                                    :
                                    isClientSubscriptionBooking
                                        ?
                                        'Book Now'
                                        :
                                        (totalAmount - couponAmount - voucherAmount + GST.amount) === 0
                                            ?
                                            'Book Now'
                                            :
                                            `Pay ₹${totalAmount - couponAmount - voucherAmount + GST.amount} & Book Now`
                            }
                        </button>
                    </ErrorBoundary>
                    <br />
                    <text>*Payments are powered by Razorpay</text>
                </div >
            </div>
    )
}
export default MakePayment;